import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import EnquiryForm from "@/components/EnquiryForm";
import CTABanner from "@/components/CTABanner";
import FAQSection from "@/components/FAQSection";
import { LIVEIN_FAQS } from "@/lib/faq";

const Tick = () => (
  <span className="mt-1 w-6 h-6 rounded-full bg-[var(--ss-gold)]/20 text-[var(--ss-gold-dark)] flex items-center justify-center text-xs shrink-0">
    <i className="fa-solid fa-check"></i>
  </span>
);

const LiveInCarePage = () => {
  return (
    <main data-testid="page-live-in-care">
      <PageHero
        eyebrow="Live-In Care"
        title="A familiar face. A familiar home. Around the clock."
        subtitle="One dedicated, fully-trained carer who lives with your loved one — keeping the routines they love, in the home they love. The thoughtful alternative to a care home."
      />

      {/* Intro grid */}
      <section className="section">
        <div className="container-px max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <span className="eyebrow">What it really looks like</span>
            <h2 className="font-display text-4xl md:text-5xl mt-4 text-[var(--ss-teal-800)]">
              Care that fits around their life — not the other way round.
            </h2>
            <p className="mt-5 text-lg text-[var(--ss-ink-soft)]">
              Live-in care means one of our trained, hand-picked carers moves
              into your loved one's home. They're there for personal care,
              cooking favourite meals, gentle company, getting to the GP, and
              everything in between.
            </p>
            <p className="mt-3 text-lg text-[var(--ss-ink-soft)]">
              Most importantly: their world doesn't shrink. The garden, the
              neighbours, the cat, the photographs on the mantelpiece — all
              stay. We bring the support to them.
            </p>

            <ul className="mt-8 space-y-4">
              {[
                ["Personal care, with dignity", "Bathing, dressing, continence support — all delivered by the same trusted carer."],
                ["Companionship that's real", "Walks, crosswords, Sunday roasts. Carers matched on hobbies and personality, not just shifts."],
                ["Medication & GP support", "Reminders, ordering repeats, coming with you to appointments."],
                ["Light housekeeping & meals", "A clean, calm home and meals cooked the way they like."],
                ["Respite & emergency cover", "From a fortnight while the family travels, to short-notice cover after a hospital stay."],
                ["Couples' care", "Two people, one home, one carer — at one combined rate."],
              ].map(([title, body]) => (
                <li key={title} className="flex gap-3">
                  <Tick />
                  <div>
                    <div className="font-semibold text-[var(--ss-teal-800)]">{title}</div>
                    <div className="text-[var(--ss-ink-soft)] text-sm">{body}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Sticky enquiry */}
          <aside className="lg:col-span-5 lg:sticky lg:top-32">
            <div className="bg-[var(--ss-cream)] rounded-3xl p-7 border border-[var(--ss-gold)]/30 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[var(--ss-teal-700)] text-[var(--ss-cream)] flex items-center justify-center">
                  <i className="fa-regular fa-comments"></i>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--ss-ink-soft)]">
                    Live-in enquiry
                  </div>
                  <div className="font-serif text-2xl text-[var(--ss-teal-800)]">
                    Talk to us today
                  </div>
                </div>
              </div>
              <p className="mt-3 text-sm text-[var(--ss-ink-soft)]">
                A senior carer will call back within one working day. No pressure,
                no salespeople — just a real conversation.
              </p>
              <div className="mt-5">
                <EnquiryForm defaultService="live-in" sourcePage="live-in-care" />
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Live-in vs care home */}
      <section className="section bg-[var(--ss-cream)]">
        <div className="container-px max-w-7xl mx-auto">
          <div className="max-w-2xl">
            <span className="eyebrow">Live-in vs care home</span>
            <h2 className="font-display text-4xl md:text-5xl mt-4 text-[var(--ss-teal-800)]">
              The honest comparison.
            </h2>
          </div>

          <div className="mt-10 grid md:grid-cols-2 gap-6">
            <div className="card-soft border-[var(--ss-gold)]/40">
              <div className="text-xs uppercase tracking-[0.25em] text-[var(--ss-gold-dark)]">
                SeaShells Live-In
              </div>
              <h3 className="font-serif text-3xl text-[var(--ss-teal-800)] mt-2">
                Their home, their rules
              </h3>
              <ul className="mt-5 space-y-3 text-[var(--ss-ink)]">
                {[
                  "One dedicated carer — they know your parent",
                  "Familiar surroundings, neighbours, pets, garden",
                  "Care plan written around their routine",
                  "No visiting hours — family visits whenever",
                  "Often comparable cost to a quality care home",
                ].map((t) => (
                  <li key={t} className="flex gap-3">
                    <Tick />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="card-soft">
              <div className="text-xs uppercase tracking-[0.25em] text-[var(--ss-ink-soft)]">
                Typical care home
              </div>
              <h3 className="font-serif text-3xl text-[var(--ss-ink)] mt-2">
                Their home, their rules — gone
              </h3>
              <ul className="mt-5 space-y-3 text-[var(--ss-ink-soft)]">
                {[
                  "Different carer every shift",
                  "New room, new routine, new everything",
                  "Schedules dictated by the home",
                  "Strict visiting hours, often shared rooms",
                  "Frequent moves between floors as needs change",
                ].map((t) => (
                  <li key={t} className="flex gap-3">
                    <span className="mt-1 w-6 h-6 rounded-full bg-[var(--ss-line)] text-[var(--ss-ink-soft)] flex items-center justify-center text-xs shrink-0">
                      <i className="fa-solid fa-minus"></i>
                    </span>
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing section removed — pricing is discussed during the free assessment instead. */}

      <CTABanner
        eyebrow="Still have questions?"
        heading="Live-in care, explained over a cup of tea."
        body="Bring the questions. We'll bring the biscuits and the experience. No pressure, no obligation."
      />

      <FAQSection
        eyebrow="Live-in care · in depth"
        title="Everything families ask about live-in carers for the elderly."
        intro="Detailed answers to the questions we hear most often when arranging live-in care for an elderly parent."
        items={LIVEIN_FAQS}
        testIdPrefix="livein-faq"
        variant="cream"
      />
    </main>
  );
};

export default LiveInCarePage;
